/**
 * API Configuration for .NET Core Backend
 * 
 * This module provides centralized API endpoint configuration for the React frontend
 * to communicate with the separate .NET Core backend.
 * 
 * Configuration Priority:
 * 1. Runtime config from public/config.js (window.__CONFIG__)
 * 2. Runtime config from public/config.json
 * 3. App config fallbacks (routes/defaults)
 * 
 * Usage:
 * import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
 * const response = await fetch(API_ENDPOINTS.CASES);
 */

import logger from '../utils/logger';
import { getApiBasePath } from './appConfig';

// Lazy getter — resolved after loadRuntimeConfig() completes in index.js.
// Must NOT be evaluated at module-import time because ES module imports run
// before the async config fetch finishes.
export const getApiBaseUrl = () => getApiBasePath();

/** @deprecated Use getApiBaseUrl() instead. Kept for logging compatibility. */
export const API_BASE_URL = undefined;

/**
 * API Endpoints for .NET Core Backend
 * 
 * Backend: COJ.SAO4.DUIFO.Api (.NET 8.0)
 * Controllers: CaseController, PaymentController
 * Authentication: Azure AD (JWT Bearer) for admin, Anonymous for public
 *
 * All properties are getters so they resolve lazily after runtime config loads.
 */
export const API_ENDPOINTS = {
  // Case Management - CaseController routes
  // GET /case/{caseNumber} - [AllowAnonymous] get single case
  CASE_BY_NUMBER: (caseNumber) => `${getApiBaseUrl()}/case/${encodeURIComponent(caseNumber)}`,
  
  // Payment - PaymentController routes
  // POST /payment/reserve - [AllowAnonymous] reserve payment slot
  get PROCESS_PAYMENT() { return `${getApiBaseUrl()}/payment/reserve`; },
  // POST /payment/confirm - [AllowAnonymous] confirm payment & create case
  get SUBMIT_CASE() { return `${getApiBaseUrl()}/payment/confirm`; },

  // Admin routes - require Azure AD Bearer token with AppAdmin role
  // GET /case - [AdminOnly] list all cases
  get ADMIN_CASES() { return `${getApiBaseUrl()}/case`; },
  // POST /case/status?caseNumber={}&status={} - [AdminOnly] update case status
  UPDATE_CASE_STATUS: (caseNumber, status) => `${getApiBaseUrl()}/case/status?caseNumber=${encodeURIComponent(caseNumber)}&status=${encodeURIComponent(status)}`,
};

/**
 * Fetch wrapper with comprehensive error handling and logging
 * @param {string} url - API endpoint URL
 * @param {object} options - Fetch options
 * @returns {Promise<Response>}
 */
export const apiFetch = async (url, options = {}) => {
  const method = options.method || 'GET';
  const requestBody = options.body ? JSON.parse(options.body) : null;
  const startTime = performance.now();

  // Log the API request
  logger.logApiRequest(method, url, requestBody);

  const defaultOptions = {
    credentials: 'include', // Include credentials for Windows Authentication
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, { ...defaultOptions, ...options });
    const duration = Math.round(performance.now() - startTime);
    
    // Try to parse response body for logging
    let responseData = null;
    const contentType = response.headers.get('content-type');
    
    if (contentType && contentType.includes('application/json')) {
      try {
        const clonedResponse = response.clone();
        responseData = await clonedResponse.json();
      } catch (e) {
        // Unable to parse response, continue
      }
    }

    // Log the response
    logger.logApiResponse(method, url, response.status, responseData, duration);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ 
        message: `HTTP error! Status: ${response.status}` 
      }));

      // Special handling for 500 errors
      if (response.status === 500) {
        logger.log500Error(method, url, new Error(error.message || 'Internal Server Error'), requestBody);
        logger.error('Backend returned 500 error', {
          method,
          url,
          requestBody,
          errorResponse: error,
          duration,
          timestamp: new Date().toISOString()
        });
      } else if (response.status >= 400) {
        logger.warn(`HTTP ${response.status} error`, {
          method,
          url,
          status: response.status,
          errorMessage: error.message,
          duration
        });
      }

      throw new Error(error.message || `HTTP error! Status: ${response.status}`);
    }

    logger.info('API request successful', {
      method,
      url,
      status: response.status,
      duration: `${duration}ms`
    });
    
    return response;
  } catch (error) {
    const duration = Math.round(performance.now() - startTime);
    
    // Log network or fetch errors
    if (error.message.includes('Failed to fetch') || error.message.includes('Network')) {
      logger.error('Network connection error', {
        method,
        url,
        errorMessage: error.message,
        duration,
        possibleCauses: [
          'Backend server is not running',
          'CORS configuration issue',
          'Network connectivity problem',
          'Backend URL is incorrect'
        ]
      });
    } else {
      logger.error('API request failed', {
        method,
        url,
        errorMessage: error.message,
        errorStack: error.stack,
        duration
      });
    }
    
    throw error;
  }
};

/**
 * Test backend connection
 * @returns {Promise<boolean>} Connection status
 */
export const testBackendConnection = async () => {
  const baseUrl = getApiBaseUrl();
  try {
    logger.info('Testing backend connection...', { url: baseUrl });

    const response = await fetch(API_ENDPOINTS.CASE_BY_NUMBER('connection-check'), {
      method: 'GET',
      credentials: 'include',
    });

    // 404 is acceptable here: it proves the anonymous case lookup route is reachable.
    const success = response.ok || response.status === 404;
    logger.logConnectionTest(baseUrl, success, !success ? new Error(`Status: ${response.status}`) : null);

    return success;
  } catch (error) {
    logger.logConnectionTest(baseUrl, false, error);
    return false;
  }
};

const apiConfig = {
  getApiBaseUrl,
  API_ENDPOINTS,
  apiFetch,
  testBackendConnection,
  logger, // Export logger for use in components
};

export default apiConfig;
